import React, { useEffect, useState, useRef } from 'react';
import { HeroBanner } from '../components/HeroBanner';
import { BottomNav } from '../components/BottomNav';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '../context/AppContext';
import { Info, X, Gem, Star, Shield, Award, FileCheck, Clock, ArrowRight, ChevronRight, TrendingUp, Check } from 'lucide-react';
interface HomePageProps {
  onNavigate: (page: string, actionId?: any) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [showDiamondTooltip, setShowDiamondTooltip] = useState(false);
  const [showStarTooltip, setShowStarTooltip] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const diamondRef = useRef<HTMLDivElement>(null);
  const starRef = useRef<HTMLDivElement>(null);
  const {
    wallet,
    actions
  } = useApp();
  // Filter actions to show planned + pending_approval
  const visibleActions = actions.filter(action => action.status === 'planned' || action.status === 'pending_approval');
  // Get user's action titles to filter suggested actions
  const userActionTitles = actions.map(a => a.title.toLowerCase());
  // Suggested actions - filter out ones user already has
  const allSuggestedActions = [{
    id: 'sug-1',
    emoji: '💡',
    title: 'Installare lampadine LED',
    points: 20,
    type: 'gold' as const,
    tag: '💡 Facile da iniziare',
    difficulty: 'Easy' as const,
    time: '1 ora'
  }, {
    id: 'sug-2',
    emoji: '🌳',
    title: 'Piantare alberi',
    points: 80,
    type: 'diamond' as const,
    tag: '🔥 Popolare nella community',
    difficulty: 'Medium' as const,
    time: '2 ore'
  }, {
    id: 'sug-3',
    emoji: '🚌',
    title: 'Usare i mezzi pubblici',
    points: 30,
    type: 'gold' as const,
    tag: '✨ Simile alle tue azioni',
    difficulty: 'Easy' as const,
    time: '30 min'
  }, {
    id: 'sug-4',
    emoji: '🍎',
    title: 'Comprare prodotti locali',
    points: 25,
    type: 'gold' as const,
    tag: '💡 Facile da iniziare',
    difficulty: 'Easy' as const,
    time: '30 min'
  }, {
    id: 'sug-5',
    emoji: '🚿',
    title: 'Fare docce brevi',
    points: 15,
    type: 'gold' as const,
    tag: '✨ Simile alle tue azioni',
    difficulty: 'Easy' as const,
    time: '15 min'
  }];
  const suggestedActions = allSuggestedActions.filter(action => !userActionTitles.includes(action.title.toLowerCase())).slice(0, 3);
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowImpactModal(false);
      setIsClosing(false);
    }, 300);
  };
  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (diamondRef.current && !diamondRef.current.contains(e.target as Node)) {
        setShowDiamondTooltip(false);
      }
      if (starRef.current && !starRef.current.contains(e.target as Node)) {
        setShowStarTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 280);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-557">
      {/* Transparent Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-558">
        <div className="flex items-center" data-id="element-559">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-560" />
        </div>
        <div className="flex items-center gap-3" data-id="element-561">
          {/* Diamond credit button with tooltip */}
          <div ref={diamondRef} className="relative" data-id="element-562">
            <button onClick={() => {
            setShowDiamondTooltip(!showDiamondTooltip);
            setShowStarTooltip(false);
          }} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-563">
              <Gem className="h-5 w-5" strokeWidth={2} data-id="element-564" />
              <span className="text-sm font-normal" data-id="element-565">{wallet.diamonds}</span>
            </button>
            {showDiamondTooltip && <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50" data-id="element-566">
                <div className="flex items-center gap-2 mb-2" data-id="element-567">
                  <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-568" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-569">
                    Crediti Diamond
                  </p>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-2" data-id="element-570">
                  Valore monetario ufficiale. Ottenuti da azioni certificate su
                  Blockchain.
                </p>
                <div className="space-y-1" data-id="element-571">
                  <p className="text-xs text-gray-500" data-id="element-572">
                    ✓ Convertibili in buoni acquisto
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-573">
                    ✓ Convertibili in denaro
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-574">
                    ✓ Certificati su Blockchain
                  </p>
                </div>
                {/* Arrow */}
                <div className="absolute -top-1.5 right-3 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" data-id="element-575" />
              </div>}
          </div>

          {/* Star credit button with tooltip */}
          <div ref={starRef} className="relative" data-id="element-576">
            <button onClick={() => {
            setShowStarTooltip(!showStarTooltip);
            setShowDiamondTooltip(false);
          }} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-577">
              <Star className="h-5 w-5" strokeWidth={2} data-id="element-578" />
              <span className="text-sm font-normal" data-id="element-579">{wallet.stars}</span>
            </button>
            {showStarTooltip && <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50" data-id="element-580">
                <div className="flex items-center gap-2 mb-2" data-id="element-581">
                  <Star className="h-4 w-4 text-yellow-500" strokeWidth={2} fill="currentColor" data-id="element-582" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-583">
                    Crediti Stelle
                  </p>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-2" data-id="element-584">
                  Valore non monetario. Ottenuti da azioni auto-certificate.
                </p>
                <div className="space-y-1" data-id="element-585">
                  <p className="text-xs text-gray-500" data-id="element-586">
                    ✓ Sblocco feature premium
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-587">
                    ✓ Accesso a eventi esclusivi
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-588">✓ Contenuti riservati</p>
                </div>
                {/* Arrow */}
                <div className="absolute -top-1.5 right-3 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" data-id="element-589" />
              </div>}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-590">
        {/* Hero Banner */}
        <HeroBanner name="Alex" avatar="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" weeklyImpact={85} completedActions={12} weeklyGoal={15} onShowCommunityImpact={() => setShowImpactModal(true)} data-id="element-591" />

        <div className="px-6 py-6 space-y-8 max-w-md mx-auto pb-28" data-id="element-592">
          {/* GB Intelligence CTA */}
          <div className="p-[2px]" style={{
          background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
          borderRadius: '100px'
        }} data-id="element-593">
            <button onClick={() => onNavigate('gb-intelligence')} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-50 transition-colors font-medium text-sm text-gray-900" style={{
            borderRadius: '100px'
          }} data-id="element-594">
              Chiedi a GB Intelligence
            </button>
          </div>

          {/* Planned Actions Reminder Widget */}
          <section data-id="element-595">
            <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-596">
              <div className="flex items-center justify-between mb-4" data-id="element-597">
                <div data-id="element-598">
                  <h2 className="font-normal text-lg text-gray-900" data-id="element-599">
                    Azioni pianificate
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-600">
                    {visibleActions.filter(a => a.status === 'planned').length}{' '}
                    di {visibleActions.length} da completare
                  </p>
                </div>
              </div>

              <div className="space-y-3" data-id="element-601">
                {visibleActions.map(action => <div key={action.id} className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-602">
                    <div className="flex items-start gap-2" data-id="element-603">
                      <div className="text-2xl" data-id="element-604">{action.emoji}</div>
                      <div className="flex-1" data-id="element-605">
                        <p className="font-normal text-sm text-gray-900" data-id="element-606">
                          {action.title}
                        </p>
                        {action.scheduledAt && <p className="text-xs text-gray-500" data-id="element-607">
                            📅 {action.scheduledAt}
                          </p>}
                        {action.proof && <div className="flex items-center gap-1 mt-1" data-id="element-608">
                            <FileCheck className="h-3 w-3 text-gray-400" data-id="element-609" />
                            <p className="text-xs text-gray-500" data-id="element-610">
                              {action.proof.filename}
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pl-9" data-id="element-611">
                      {action.status === 'pending_approval' && <span className="flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full" data-id="element-612">
                          <Clock className="h-3 w-3" data-id="element-613" />
                          In attesa
                        </span>}
                      {action.status === 'planned' && <div className="ml-auto" data-id="element-614">
                          <button onClick={() => onNavigate('actions', action.id)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-615">
                            <span className="flex items-center gap-1" data-id="element-616">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-617" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-618" />}
                              <span data-id="element-619">+{action.points}</span>
                            </span>
                          </button>
                        </div>}
                      {action.status === 'pending_approval' && <div className="ml-auto" data-id="element-620">
                          <button disabled className="px-3 py-1.5 rounded-full text-xs font-normal bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-621">
                            In verifica
                          </button>
                        </div>}
                    </div>
                  </div>)}
              </div>

              <button onClick={() => onNavigate('impact')} className="w-full mt-4 text-sm text-green-600 font-normal hover:underline text-center" data-id="element-622">
                Pianifica altre azioni
              </button>
            </div>
          </section>

          {/* Suggested Actions Widget */}
          {suggestedActions.length > 0 && <section data-id="element-623">
              <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-624">
                <div className="flex items-center justify-between mb-4" data-id="element-625">
                  <div data-id="element-626">
                    <h2 className="font-normal text-lg" data-id="element-627">Suggerite per te</h2>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-628">
                      Basate sulla community e le tue abitudini
                    </p>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-629">
                  {suggestedActions.map(action => <button key={action.id} onClick={() => onNavigate('activity-detail', {
                id: action.id,
                emoji: action.emoji,
                title: action.title,
                points: action.points,
                type: action.type,
                difficulty: action.difficulty,
                time: action.time,
                description: `Scopri come completare questa azione sostenibile e guadagna ${action.points} punti.`
              })} className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors cursor-pointer text-left" data-id="element-630">
                      <div className="text-2xl" data-id="element-631">{action.emoji}</div>
                      <div className="flex-1" data-id="element-632">
                        <p className="font-normal text-sm text-gray-900" data-id="element-633">
                          {action.title}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-634">{action.tag}</p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-635">
                        <div className={`flex items-center gap-1 text-xs font-normal ${action.type === 'diamond' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-636">
                          {action.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-637" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-638" />}
                          <span data-id="element-639">+{action.points}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-300" data-id="element-640" />
                      </div>
                    </button>)}
                </div>

                <button onClick={() => onNavigate('impact')} className="w-full mt-4 text-sm text-green-600 font-normal hover:underline text-center" data-id="element-641">
                  Vedi tutte le azioni
                </button>
              </div>
            </section>}

          {/* Recent Activity */}
          <section data-id="element-642">
            <h2 className="font-normal text-lg mb-3" data-id="element-643">Attività recenti</h2>
            <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-4" data-id="element-644">
              <div className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors" data-id="element-645">
                <div className="flex items-center gap-3" data-id="element-646">
                  <div className="text-2xl" data-id="element-647">🚲</div>
                  <div data-id="element-648">
                    <p className="font-normal text-sm text-gray-900" data-id="element-649">
                      In bici al lavoro
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-650">Oggi, 8:30</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-normal text-green-600" data-id="element-651">
                  <Gem className="h-4 w-4" strokeWidth={2} data-id="element-652" />
                  <span data-id="element-653">+50</span>
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-654" />

              <div className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors" data-id="element-655">
                <div className="flex items-center gap-3" data-id="element-656">
                  <div className="text-2xl" data-id="element-657">♻️</div>
                  <div data-id="element-658">
                    <p className="font-normal text-sm text-gray-900" data-id="element-659">
                      Riciclato il vetro
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-660">Ieri, 14:20</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-normal text-gray-400" data-id="element-661">
                  <Star className="h-4 w-4" strokeWidth={2} data-id="element-662" />
                  <span data-id="element-663">+10</span>
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-664" />

              {/* Vedi tutte link */}
              <button onClick={() => onNavigate('actions', 'recent-tab')} className="w-full flex items-center justify-center gap-1 text-sm text-green-600 font-normal hover:underline pt-1" data-id="element-665">
                Vedi tutte
                <ArrowRight className="h-4 w-4" data-id="element-666" />
              </button>
            </div>
          </section>
        </div>
      </ScrollArea>

      {/* Navigation */}
      <BottomNav activePage="home" onNavigate={onNavigate} data-id="element-667" />

      {/* Impact Details Modal — Weekly Breakdown */}
      {showImpactModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseModal} data-id="element-668" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[80vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-669">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-670">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-671" />
              <div className="flex items-center justify-between" data-id="element-672">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-673">
                  Impatto settimanale
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-674">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-675" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-676">
                Basato su 12 azioni completate su 15 obiettivo settimanale
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-677">
              <div className="space-y-6" data-id="element-678">
                {/* Score overview */}
                <div className="grid grid-cols-3 gap-3" data-id="element-679">
                  <div className="bg-green-50 rounded-xl p-4 border border-green-100 text-center" data-id="element-680">
                    <p className="text-2xl font-normal text-green-600" data-id="element-681">85%</p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-682">Il tuo score</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center" data-id="element-683">
                    <p className="text-2xl font-normal text-gray-700" data-id="element-684">12</p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-685">Completate</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center" data-id="element-686">
                    <p className="text-2xl font-normal text-gray-700" data-id="element-687">15</p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-688">Obiettivo</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div data-id="element-689">
                  <div className="flex items-center justify-between mb-2" data-id="element-690">
                    <span className="text-sm text-gray-700" data-id="element-691">
                      Progresso settimanale
                    </span>
                    <span className="text-sm font-medium text-green-600" data-id="element-692">
                      12/15
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-693">
                    <div className="bg-green-600 h-full rounded-full transition-all duration-500" style={{
                  width: '80%'
                }} data-id="element-694" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5" data-id="element-695">
                    3 azioni rimanenti per raggiungere l'obiettivo
                  </p>
                </div>

                {/* Breakdown by category */}
                <div data-id="element-696">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-697">
                    Distribuzione per categoria
                  </h4>
                  <div className="space-y-3" data-id="element-698">
                    {[{
                  emoji: '🚗',
                  label: 'Trasporti',
                  completed: 4,
                  goal: 5
                }, {
                  emoji: '♻️',
                  label: 'Rifiuti',
                  completed: 3,
                  goal: 3
                }, {
                  emoji: '⚡',
                  label: 'Energia',
                  completed: 2,
                  goal: 4
                }, {
                  emoji: '🍽️',
                  label: 'Alimentazione',
                  completed: 2,
                  goal: 2
                }, {
                  emoji: '💧',
                  label: 'Acqua',
                  completed: 1,
                  goal: 1
                }].map(cat => <div key={cat.label} data-id="element-699">
                        <div className="flex items-center justify-between mb-1" data-id="element-700">
                          <div className="flex items-center gap-2" data-id="element-701">
                            <span className="text-base" data-id="element-702">{cat.emoji}</span>
                            <span className="text-xs text-gray-700" data-id="element-703">
                              {cat.label}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500" data-id="element-704">
                            {cat.completed}/{cat.goal}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-705">
                          <div className={`h-full rounded-full ${cat.completed >= cat.goal ? 'bg-green-500' : 'bg-green-400'}`} style={{
                      width: `${Math.min(cat.completed / cat.goal * 100, 100)}%`
                    }} data-id="element-706" />
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Goals */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-707">
                  <div className="flex items-start gap-2" data-id="element-708">
                    <TrendingUp className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-709" />
                    <div data-id="element-710">
                      <p className="text-xs font-medium text-blue-900 mb-1" data-id="element-711">
                        Obiettivo settimanale
                      </p>
                      <p className="text-xs text-blue-800 leading-relaxed" data-id="element-712">
                        Completa ancora <strong data-id="element-713">3 azioni</strong> per
                        raggiungere il 100% del tuo obiettivo settimanale e
                        guadagnare un bonus di <strong data-id="element-714">50 Diamond</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Encouragement */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-100" data-id="element-715">
                  <p className="text-sm font-normal text-green-800" data-id="element-716">
                    🌟 Sei sopra la media della community (72%)!
                  </p>
                  <p className="text-xs text-green-700 mt-1" data-id="element-717">
                    Continua così per restare al top
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}